import React, { useState } from 'react';
import { Package } from 'lucide-react';
import { getEffectiveUnits, formatStockFromUnits, getMaxUnitsFor } from '../../utils/formatStock';

export default function SaleUnitSelector({ product, onConfirm, onCancel }) {
  const units = getEffectiveUnits(product || {});
  const sellableIndexes = units
    .map((u, i) => ({ ...u, i }))
    .filter((u) => u.sellable && Number(u.salePrice) > 0)
    .map((u) => u.i);

  const [selectedIndex, setSelectedIndex] = useState(sellableIndexes[0] ?? 0);
  const [quantity, setQuantity] = useState(1);

  const selected = units[selectedIndex] || units[0];
  const price = Number(selected.salePrice) || 0;
  const stock = product.currentStock ?? product.stock ?? product.quantity ?? 0;
  // Same inventory rule the cart and the backend enforce: never more units
  // than the stock on hand.
  const maxQuantity = Math.max(1, getMaxUnitsFor(product, selected?.name));
  const cappedQuantity = Math.min(quantity, maxQuantity);
  const lineTotal = price * cappedQuantity;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <Package size={20} className="text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{product.productName || product.name}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Stock: {formatStockFromUnits(stock, units)}
          </p>
        </div>
      </div>

      {sellableIndexes.length > 1 && (
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Sale Unit</label>
          <div className="grid grid-cols-2 gap-2">
            {sellableIndexes.map((idx) => {
              const u = units[idx];
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedIndex(idx)}
                  className={`px-3 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                    selectedIndex === idx
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div>{u.name}</div>
                  <div className="text-xs opacity-75">Rs. {(Number(u.salePrice) || 0).toLocaleString()}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Quantity</label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            -
          </button>
          <input
            type="number"
            min="1"
            max={maxQuantity}
            value={cappedQuantity}
            onChange={(e) => setQuantity(Math.min(maxQuantity, Math.max(1, parseInt(e.target.value) || 1)))}
            className="flex-1 text-center h-9 text-sm font-medium bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary-500"
          />
          <button
            type="button"
            onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
            disabled={cappedQuantity >= maxQuantity}
            className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            +
          </button>
        </div>
        <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
          Max {maxQuantity} {selected?.name} available
        </p>
      </div>

      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {cappedQuantity} x {selected.name}
        </span>
        <span className="text-lg font-bold text-gray-900 dark:text-white">
          Rs. {lineTotal.toLocaleString()}
        </span>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm({ unit: selected.name, quantity: cappedQuantity, price })}
          className="flex-1 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
}